import upperFirst from 'lodash/upperFirst'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TableCellProps } from 'react-virtualized'

import { LightPolicy, Provider } from 'src/api/gen/api'
import { dateToDayJSLocalized } from 'src/commons/temporalHelpers'
import { CellLink } from 'src/components/Table/Cells'
import { FullExportableTable, ISearchBar } from 'src/components/Table/Templates/FullExportableTable'
import { useProviders, useProviderUser } from 'src/layout/hooks'

export const useProvidersData = () => {
  const { allProviders, activeProviders } = useProviders()
  const [providerNames, setProviderNames] = useState<Map<string, string>>(new Map())

  const mapProviderNames = () => {
    const mapping = new Map()
    allProviders.forEach((provider: Provider) => mapping.set(provider.id, provider.name))
    setProviderNames(mapping)
  }

  useEffect(() => {
    mapProviderNames()
  }, [allProviders])

  return { activeProviders, providerNames }
}

interface IProps {
  className?: string
  data: LightPolicy[]
  loading?: boolean
  onClickExport?: (event: React.MouseEvent<HTMLElement>) => void
  searchBar: ISearchBar
  totalCount: number
}

const FeeRatesTable = (props: IProps) => {
  const { t } = useTranslation()
  const isProviderUser = useProviderUser()
  const { activeProviders, providerNames } = useProvidersData()

  const columns = [
    {
      label: upperFirst(t('titles.name.fee_rates')),
      width: 380,
      dataKey: 'name',
      cellRenderer: (cellProps: TableCellProps) => {
        const policy = cellProps.rowData as LightPolicy
        return (
          <CellLink url={`/fee_rate/${policy.id}/details`} tooltip>
            {policy.name}
          </CellLink>
        )
      },
    },
    // Skip the description column
    {
      label: upperFirst(t('titles.kind.fee_rates')),
      width: 380,
      dataKey: 'kind',
      cellRenderer: (cellProps: TableCellProps) => t(`policy.kind.${cellProps.cellData}`),
    },
    {
      label: upperFirst(t('titles.providers', { count: activeProviders.length })),
      width: 380,
      dataKey: 'providers',
      cellRenderer: (cellProps: TableCellProps) =>
        cellProps.cellData.map((providerId: string) => providerNames.get(providerId) || providerId).join(', '),
    },
    {
      label: upperFirst(t('titles.start_datetime')),
      width: 380,
      dataKey: 'start_date',
      cellRenderer: (cellProps: TableCellProps) => dateToDayJSLocalized(cellProps.cellData).format('L LT'),
    },
    {
      label: upperFirst(t('titles.end_datetime')),
      width: 380,
      dataKey: 'end_date',
      cellRenderer: (cellProps: TableCellProps) =>
        cellProps.cellData ? dateToDayJSLocalized(cellProps.cellData).format('L LT') : '',
    },
    {
      label: upperFirst(t('titles.published_datetime')),
      width: 380,
      dataKey: 'published_date',
      cellRenderer: (cellProps: TableCellProps) =>
        cellProps.cellData ? dateToDayJSLocalized(cellProps.cellData).format('L LT') : t('status.not_published'),
    },
    {
      label: upperFirst(t('titles.due_date')),
      width: 380,
      dataKey: 'due_date',
      cellRenderer: (cellProps: TableCellProps) =>
        cellProps.cellData ? dateToDayJSLocalized(cellProps.cellData).format('L LT') : '',
    },
  ]

  if (isProviderUser && activeProviders.length > 2) {
    // we remove the provider
    columns.splice(2, 1)
  }

  return (
    <FullExportableTable
      data={props.data}
      noDataMessage={t('status.no_data.fee_rates')}
      totalCount={props.totalCount}
      columns={columns}
      className={props.className}
      onClickExport={props.onClickExport}
      isLoading={props.loading}
      searchBar={props.searchBar}
    />
  )
}

export default FeeRatesTable
